import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Modal } from 'antd';
import { EyeOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { baseUrlImage, baseUrVideos } from '../../../../baseUrl';
import { clodinaryImage, updateUser } from '../../../../api/login/Login';
import dummyAdhaarFront from "../../../../assets/images/kyc/adhaarFront.png";
import dummyAdhaarBack from "../../../../assets/images/kyc/adhharBack.png";
import gst from "../../../../assets/images/kyc/kycCirtificate.png";
import pancard from "../../../../assets/images/kyc/panCard.png";
import bankProof from "../../../../assets/images/kyc/bankProof.png";
import kycVid from "../../../../assets/images/kyc/kycVideo.png";
import shopinside from "../../../../assets/images/kyc/shop-inside.jpeg";
import shopoutside from "../../../../assets/images/kyc/blankshop1.png";
import KycModals from './kycModals/KycModals';
import "../kycDetails/KycDetails.css";

const ModalPdfShow = ({ handleClose, url }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleScroll = (e) => {
    const element = e.target;
    const scrollPosition = element.scrollTop;
    const viewportHeight = element.clientHeight;
    const totalHeight = element.scrollHeight;

    const newPage = Math.ceil((scrollPosition / (totalHeight - viewportHeight)) * totalPages);
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>GST Document Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex">
          <div style={{ height: "80vh", width: "100%" }} onScroll={handleScroll}>
            <iframe
              src={`${baseUrlImage}${url}#toolbar=0`}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="PDF Preview"
              onLoad={(e) => {
                try {
                  const doc = e.target.contentDocument;
                  if (doc) {
                    setTotalPages(doc.body.getElementsByTagName('page').length || 1);
                  }
                } catch (error) {
                  console.log('Could not determine total pages');
                }
              }}
            />
          </div>
        </div>
      </Modal.Body>
    </>
  );
};

export const DocumentCard = ({
  title,
  fieldKey,
  imageSrc,
  dummyImage,
  onClick,
  onUpload,
  isUploading,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(fieldKey, title, file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Card 
      hoverable 
      className="h-100 shadow-sm"
      bodyStyle={{ padding: '12px' }}
    >
      <div className="text-center mb-2">
        <h5 className="fw-bold text-primary p-2 bg-light rounded">{title}</h5>
      </div>
      <div 
        className="position-relative cursor-pointer" 
        onClick={onClick}
        style={{
          height: '180px',
          overflow: 'hidden',
          borderRadius: '4px',
          border: '1px solid #e8e8e8',
          backgroundColor: '#fafafa'
        }}
      >
        <img
          src={imageSrc || dummyImage}
          alt={title}
          className="w-100 h-100 object-fit-cover"
        />
        {imageSrc && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
            }}
          >
            <EyeOutlined className="text-white fs-5" />
          </div>
        )}
        {!imageSrc && (
          <div className="position-absolute bottom-0 start-0 w-100 bg-light bg-opacity-75 p-2">
            <p className="mb-0 text-center text-muted">
              <small>{title} Not Found</small>
            </p>
          </div>
        )}
      </div>

      {onUpload && (
        <div className="mt-2 text-center">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <button
            type="button"
            className="btn btn-outline-primary btn-sm w-100"
            onClick={(e) => {
              e.stopPropagation();
              if (fileInputRef.current) fileInputRef.current.click();
            }}
            disabled={isUploading}
          >
            {isUploading ? (
              <span>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Uploading...
              </span>
            ) : (
              <span>
                <EditOutlined className="me-1" />
                {imageSrc ? "Replace Image" : "Upload Image"}
              </span>
            )}
          </button>
        </div>
      )}
    </Card>
  );
};

const KycDetails = ({ initialValues, state, userId, refreshData }) => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    visible: false,
    image: null,
    title: ''
  });
  const [docsState, setDocsState] = useState(initialValues?.docs || {});
  const [uploadingField, setUploadingField] = useState(null);

  useEffect(() => {
    if (initialValues?.docs) {
      setDocsState(initialValues.docs);
    }
  }, [initialValues]);

  const handlePreview = (url, title) => {
    if (!url) return;
    setPreviewModal({
      visible: true,
      image: url,
      title
    });
  };

  const handleDocumentUpload = async (fieldKey, fieldTitle, file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file (JPG, PNG, JPEG, WEBP)");
      return;
    }

    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploadingField(fieldKey);

      // 1. Upload to S3
      const uploadRes = await clodinaryImage(formData);
      const uploadedUrl = uploadRes?.data?.url || uploadRes?.data?.data?.url;

      if (!uploadedUrl) {
        toast.error(`Failed to upload ${fieldTitle}`);
        setUploadingField(null);
        return;
      }

      // 2. Update DB via updateUser
      const res = await updateUser(userId, {
        kycDetails: {
          [fieldKey]: uploadedUrl,
        },
      });

      if (
        res?.statusCode == "200" ||
        res?.status_Code == "200" ||
        res?.status === 200 ||
        res?.data?.statusCode == "200"
      ) {
        setDocsState((prev) => ({
          ...prev,
          [fieldKey]: uploadedUrl,
        }));
        toast.success(`${fieldTitle} replaced and saved successfully!`);
        if (typeof refreshData === "function") {
          refreshData();
        }
      } else {
        toast.error(res?.message || `Failed to update ${fieldTitle} in database`);
      }
    } catch (err) {
      console.error(`Error uploading ${fieldTitle}:`, err);
      toast.error(
        err?.response?.data?.message || err?.message || `Failed to update ${fieldTitle}`
      );
    } finally {
      setUploadingField(null);
    }
  };

  return (
    <div className="p-4">
      <Card className="shadow-sm">
        <h3 className="mb-4">KYC Documentation</h3>
        
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="Aadhaar Card Front"
              fieldKey="adhaar_front_card"
              imageSrc={docsState?.adhaar_front_card || null}
              dummyImage={dummyAdhaarFront}
              onClick={() => handlePreview(docsState?.adhaar_front_card, 'Aadhaar Front')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "adhaar_front_card"}
            />
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="Aadhaar Card Back"
              fieldKey="adhaar_back_card"
              imageSrc={docsState?.adhaar_back_card || null}
              dummyImage={dummyAdhaarBack}
              onClick={() => handlePreview(docsState?.adhaar_back_card, 'Aadhaar Back')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "adhaar_back_card"}
            />
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="PAN Card"
              fieldKey="pan_card"
              imageSrc={docsState?.pan_card || null}
              dummyImage={pancard}
              onClick={() => handlePreview(docsState?.pan_card, 'PAN Card')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "pan_card"}
            />
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="Bank Proof"
              fieldKey="bank_proof"
              imageSrc={docsState?.bank_proof || null}
              dummyImage={bankProof}
              onClick={() => handlePreview(docsState?.bank_proof, 'Bank Proof')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "bank_proof"}
            />
          </Col>

          {docsState?.gst && (
            <Col xs={24} sm={12} lg={8} xl={6}>
              <DocumentCard
                title="GST Certificate"
                fieldKey="gst"
                imageSrc={docsState?.gst || null}
                dummyImage={gst}
                onClick={() => handlePreview(docsState?.gst, 'GST Certificate')}
                onUpload={handleDocumentUpload}
                isUploading={uploadingField === "gst"}
              />
            </Col>
          )}

          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="Shop Inside"
              fieldKey="shop_internal_photo"
              imageSrc={docsState?.shop_internal_photo || null}
              dummyImage={shopinside}
              onClick={() => handlePreview(docsState?.shop_internal_photo, 'Shop Inside')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "shop_internal_photo"}
            />
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <DocumentCard
              title="Shop Outside"
              fieldKey="shop_outside_photo"
              imageSrc={docsState?.shop_outside_photo || null}
              dummyImage={shopoutside}
              onClick={() => handlePreview(docsState?.shop_outside_photo, 'Shop Outside')}
              onUpload={handleDocumentUpload}
              isUploading={uploadingField === "shop_outside_photo"}
            />
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card className="h-100 shadow-sm" bodyStyle={{ padding: '12px' }}>
              <div className="text-center mb-3">
                <h5 className="fw-bold text-primary p-2 bg-light rounded">KYC Video</h5>
              </div>
              <div 
                className="position-relative cursor-pointer" 
                style={{ height: '180px', overflow: 'hidden' }}
                onClick={() => setVideoModalOpen(true)}
              >
                {state?.kycVideo ? (
                  <>
                    <video 
                      src={`${baseUrVideos}${state.kycVideo}`}
                      className="w-100 h-100 object-fit-cover"
                    />
                    <div className="position-absolute top-50 start-50 translate-middle">
                      <div className="play-btn"></div>
                    </div>
                  </>
                ) : (
                  <div className="h-100 d-flex flex-column justify-content-center align-items-center bg-light">
                    <img src={kycVid} alt="Video placeholder" className="mb-2" style={{ width: '64px' }} />
                    <p className="text-muted mb-0"><small>KYC Video Not Found</small></p>
                  </div>
                )}
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card className="h-100 shadow-sm" bodyStyle={{ padding: '12px' }}>
              <div className="text-center mb-3">
                <h5 className="fw-bold text-primary p-2 bg-light rounded">Location Details</h5>
              </div>
              <div className="p-3">
                {docsState?.longitude || docsState?.latitude ? (
                  <>
                    <p className="mb-2">
                      <strong>Longitude: </strong>
                      {docsState.longitude}
                    </p>
                    <p className="mb-0">
                      <strong>Latitude: </strong>
                      {docsState.latitude}
                    </p>
                  </>
                ) : (
                  <p className="text-muted text-center mb-0">Location Not Found</p>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      <Modal
        open={previewModal.visible}
        visible={previewModal.visible}
        title={previewModal.title}
        footer={null}
        onCancel={() => setPreviewModal({ visible: false, image: null, title: '' })}
        width={800}
        centered
      >
        {previewModal.image && (
          <img
            src={previewModal?.image}
            alt={previewModal.title}
            style={{ width: '100%', height: 'auto' }}
          />
        )}
      </Modal>

      <KycModals 
        open={videoModalOpen}
        setOpen={setVideoModalOpen}
        kycVideo={state?.kycVideo || "KYC Videos Not Found"}
      />
    </div>
  );
};

export default KycDetails;
